/**
 * Estado do rastreio e da previsão de nuvens (Zustand) — duas subcamadas do
 * Satélite. Registra o codec de URL `k` (de tracKing: `t` e `r` já têm dono)
 * no import — `ui/modules.tsx` importa este arquivo antes de `readUrlState()`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { IndiceRastreio } from './api';
import type { Sistema } from './layer';
import type { Selecao } from './selecao';
import { aoMudarRegistro } from './registro';
import { antecedenciaValida, antecedenciasNoQuadro } from './tempo';

/** O rastreio faz parte da imagem padrão (pedido do usuário, 28/09/2026); a previsão é por escolha. */
export const DEFAULT_RASTREIO = true;
export const DEFAULT_PREVISAO = false;
/** Só o limiar externo de cara (pedido do usuário, 28/09/2026); os internos são opção. */
export const DEFAULT_NUCLEOS = false;
/** Meia hora: longe o bastante para o deslocamento aparecer, perto o bastante para a persistência valer. */
export const DEFAULT_ANTECEDENCIA = 30;
/** Teto do que o link aceita (min): o dobro das 3 h que a máquina publica. */
export const ANTECEDENCIA_MAX = 360;

export type EstadoCarga = 'idle' | 'loading' | 'ready' | 'error';

export interface Situacao {
  estado: EstadoCarga;
  erro: string | null;
}

/**
 * Uma subcamada no quadro exibido:
 * `ok` na tela · `carregando` baixando (o mapa fica só com a imagem) ·
 * `sem` não há arquivo deste horário · `falhou` o arquivo não veio.
 */
export type EstadoNoQuadro = 'ok' | 'carregando' | 'sem' | 'falhou';

export interface RastreioNoQuadro {
  rastreio: EstadoNoQuadro;
  previsao: EstadoNoQuadro;
}

/** Um sistema do quadro exibido, para a lista do painel flutuante. */
export interface SistemaNaLista {
  sistema: Sistema;
  /** Ponto dentro do contorno (projeção do mapa), onde a ficha abre. */
  ponto: [number, number];
  /** Retângulo do contorno (projeção do mapa), para o mapa voar até ele. */
  extensao: [number, number, number, number];
}

const PAINEL_KEY = 'amaview:rastreio-aberto';
const ABA_KEY = 'amaview:rastreio-aba';

/** As abas do painel: o rastreio das nuvens (GOES-19) e o da chuva (GSMaP). */
export type AbaDoRastreio = 'nuvens' | 'chuva';

function lerAba(): AbaDoRastreio {
  try {
    return globalThis.localStorage?.getItem(ABA_KEY) === 'chuva' ? 'chuva' : 'nuvens';
  } catch {
    return 'nuvens';
  }
}

function gravarAba(aba: AbaDoRastreio): void {
  try {
    if (aba === 'chuva') globalThis.localStorage?.setItem(ABA_KEY, aba);
    else globalThis.localStorage?.removeItem(ABA_KEY);
  } catch {
    // navegação privada / armazenamento bloqueado: só não lembra
  }
}

function lerPainel(): boolean {
  try {
    return globalThis.localStorage?.getItem(PAINEL_KEY) === '1';
  } catch {
    return false;
  }
}

function gravarPainel(aberto: boolean): void {
  try {
    if (aberto) globalThis.localStorage?.setItem(PAINEL_KEY, '1');
    else globalThis.localStorage?.removeItem(PAINEL_KEY);
  } catch {
    // navegação privada / armazenamento bloqueado: só não lembra
  }
}

export interface TrackingState {
  /** Contornos e trajetórias do horário exibido. */
  rastreio: boolean;
  /** Contornos previstos a partir do horário exibido. */
  previsao: boolean;
  /** Antecedência escolhida (min); 0 = só a observação. É a que vai para o link. */
  antecedencia: number;
  /** Desenhar também os limiares internos (núcleos mais frios). */
  nucleos: boolean;
  /** O fluxo da previsão está tocando (observação → cada antecedência do quadro). */
  fluxo: boolean;
  /**
   * Pausado na observação (passo 0): a tela fica em "agora" sem que isso
   * substitua a antecedência escolhida. Sai ao escolher, tocar ou religar.
   */
  naObservacao: boolean;
  /**
   * A imagem na tela é a ORIGEM da previsão: a mais nova do loop que tem
   * previsão (`quadroMaisNovoComPrevisao`). Só nela a previsão de todos os
   * sistemas é desenhada — nas anteriores, previsões velhas não interessam
   * (pedido do usuário, 08/10/2026); lá só a do sistema clicado, pela ficha.
   * Mantido por `fluxo.ts`; sem ele (testes de unidade) vale true.
   */
  naOrigem: boolean;
  /** Antecedência do passo do fluxo (min), enquanto ele toca. */
  passo: number;
  situacao: Situacao;
  indice: IndiceRastreio | null;
  noQuadro: RastreioNoQuadro;
  /** Sistemas (primeiro limiar) do quadro na tela, do maior para o menor. */
  sistemas: SistemaNaLista[];
  /**
   * Antecedências (`h`, min) do arquivo de previsão NA TELA; null sem
   * arquivo, vazia se ele veio sem contorno. Arquivos antigos têm passo
   * diferente do que o índice declara.
   */
  hNoQuadro: number[] | null;
  /** Painel flutuante aberto (lembrado entre visitas). */
  painelAberto: boolean;
  /** A aba do painel (lembrada entre visitas). */
  aba: AbaDoRastreio;
  setAba: (aba: AbaDoRastreio) => void;
  /** Folha do celular aberta. Não é lembrada: abrir o app com a folha sobre o mapa atrapalha. */
  folhaAberta: boolean;
  /** Lista "Sistemas nesta imagem" expandida, e inteira. Valem enquanto a página está aberta. */
  listaAberta: boolean;
  listaInteira: boolean;
  /** "Mais detalhes" da ficha compacta do celular aberto. Vale enquanto a página está aberta, como a lista. */
  fichaDetalhes: boolean;

  setPainelAberto: (aberto: boolean) => void;
  setFolhaAberta: (aberta: boolean) => void;
  setListaAberta: (aberta: boolean) => void;
  setListaInteira: (inteira: boolean) => void;
  setFichaDetalhes: (abertos: boolean) => void;
  setRastreio: (on: boolean) => void;
  setPrevisao: (on: boolean) => void;
  /**
   * Escolhe a antecedência à mão: o fluxo para nela. Zero ("agora") NÃO é
   * uma escolha que se guarde: põe a tela na observação (`naObservacao`) e
   * deixa a antecedência guardada onde estava — senão religar a previsão
   * abriria sem nada desenhado.
   */
  setAntecedencia: (min: number) => void;
  setNucleos: (on: boolean) => void;
  /** Sistema (`uid`) cuja ficha pediu os núcleos no mapa, ou null. Não vai para o link: vale enquanto a ficha está aberta. */
  nucleosDe: number | null;
  setNucleosDe: (uid: number | null) => void;
  /**
   * O sistema selecionado: o da ficha aberta (`selecao.ts`), destacado no mapa
   * e na lista. Estado de interação: não vai para o link nem para as preferências.
   */
  selecao: Selecao | null;
  setSelecao: (selecao: Selecao | null) => void;
  /**
   * Antecedência (min) escolhida na FICHA do sistema selecionado com a Previsão
   * desligada: só ele é projetado no mapa, os demais não (pedido do usuário,
   * 08/10/2026). Null = nenhuma. Some com a seleção; não vai para o link.
   */
  antecedenciaDaSelecao: number | null;
  setAntecedenciaDaSelecao: (min: number | null) => void;
  /** Muda quando um quadro entra na memória ou sai dela (`registro.ts`). */
  versaoDoRegistro: number;
  /**
   * Toca (a partir da observação) ou PAUSA o fluxo. Pausar é ato do usuário
   * sobre o que ele vê: o passo na tela vira a antecedência escolhida — menos
   * na observação (passo 0), que fica na tela sem apagar a escolha.
   */
  setFluxo: (tocando: boolean) => void;
  /**
   * Tira o fluxo de cena sem pausa do usuário (exportação, previsão
   * desligada): a tela volta à antecedência escolhida, que não muda.
   */
  pararFluxo: () => void;
}

type Salvo = Pick<TrackingState, 'rastreio' | 'previsao' | 'antecedencia'> & { nucleos?: boolean };

export const useTrackingStore = create<TrackingState>()((set) => ({
  rastreio: DEFAULT_RASTREIO,
  previsao: DEFAULT_PREVISAO,
  antecedencia: DEFAULT_ANTECEDENCIA,
  nucleos: DEFAULT_NUCLEOS,
  setNucleos: (nucleos) => set({ nucleos }),
  nucleosDe: null,
  setNucleosDe: (nucleosDe) => set({ nucleosDe }),
  selecao: null,
  setSelecao: (selecao) => set((s) => (s.selecao?.chave === selecao?.chave ? s : { selecao, antecedenciaDaSelecao: null })),
  antecedenciaDaSelecao: null,
  setAntecedenciaDaSelecao: (antecedenciaDaSelecao) => set({ antecedenciaDaSelecao }),
  versaoDoRegistro: 0,
  fluxo: false,
  naObservacao: false,
  naOrigem: true,
  passo: 0,
  situacao: { estado: 'idle', erro: null },
  indice: null,
  noQuadro: { rastreio: 'sem', previsao: 'sem' },
  sistemas: [],
  hNoQuadro: null,
  painelAberto: lerPainel(),
  aba: lerAba(),
  setAba: (aba) => {
    gravarAba(aba);
    set({ aba });
  },
  folhaAberta: false,
  listaAberta: false,
  listaInteira: false,
  setFolhaAberta: (folhaAberta) => set({ folhaAberta }),
  setListaAberta: (listaAberta) => set({ listaAberta }),
  setListaInteira: (listaInteira) => set({ listaInteira }),
  fichaDetalhes: false,
  setFichaDetalhes: (fichaDetalhes) => set({ fichaDetalhes }),

  setPainelAberto: (painelAberto) => {
    gravarPainel(painelAberto);
    set({ painelAberto });
  },
  setRastreio: (rastreio) => set({ rastreio }),
  // Ligar a Previsão (todos os sistemas) desfaz a projeção de um só, escolhida na ficha: desligada
  // de novo, ela não volta sozinha.
  setPrevisao: (previsao) => set(previsao ? { previsao, antecedenciaDaSelecao: null } : { previsao }),
  setAntecedencia: (min) =>
    set(() => {
      const a = antecedenciaParaGuardar(min);
      // Fora do que se pode guardar (não é número, passa do teto): só para o fluxo.
      if (a === null) return { fluxo: false };
      return a === 0 ? { fluxo: false, naObservacao: true } : { antecedencia: a, fluxo: false, naObservacao: false };
    }),
  setFluxo: (tocando) =>
    set((s) => {
      if (tocando) return { fluxo: true, passo: 0, naObservacao: false };
      if (!s.fluxo) return { fluxo: false };
      // Pausar deixa na tela o passo em que o fluxo estava; na observação, sem apagar a escolha.
      return s.passo > 0 ? { fluxo: false, antecedencia: s.passo, naObservacao: false } : { fluxo: false, naObservacao: true };
    }),
  pararFluxo: () => set({ fluxo: false, passo: 0, naObservacao: false }),
}));

aoMudarRegistro(() => useTrackingStore.setState((s) => ({ versaoDoRegistro: s.versaoDoRegistro + 1 })));

type DoQuadro = Pick<TrackingState, 'indice'> & Partial<Pick<TrackingState, 'hNoQuadro'>>;

/** As antecedências que o quadro exibido tem (as do índice, enquanto o arquivo não chega). */
export function antecedenciasDisponiveis(s: DoQuadro): number[] {
  return antecedenciasNoQuadro(s.indice?.antecedencias ?? [], s.hNoQuadro ?? null).disponiveis;
}

/**
 * A antecedência desenhada no mapa (min): a do passo do fluxo, ou a escolhida —
 * sempre uma que o quadro exibido tem (a mais próxima, se a escolhida não
 * existe nele). Zero = nenhum polígono previsto.
 */
export function antecedenciaNaTela(
  s: Pick<TrackingState, 'antecedencia' | 'fluxo' | 'passo'> & Partial<Pick<TrackingState, 'naObservacao'>> & DoQuadro,
): number {
  if (!s.fluxo && s.naObservacao) return 0;
  return antecedenciaValida(s.fluxo ? s.passo : antecedenciaEscolhida(s), antecedenciasDisponiveis(s));
}

/**
 * A antecedência guardada, nunca zero: um estado que chegue com 0 (ou lixo)
 * por qualquer caminho vale como o padrão. "Agora" é `naObservacao`.
 */
export function antecedenciaEscolhida(s: Pick<TrackingState, 'antecedencia'>): number {
  const a = antecedenciaParaGuardar(s.antecedencia);
  return a !== null && a > 0 ? a : DEFAULT_ANTECEDENCIA;
}

/**
 * A antecedência com contorno previsto DESENHADO no mapa (min), ou 0 quando
 * não há nenhum. É o que o aviso sobre o mapa, a moldura, o relógio ciano e a
 * zona da linha do tempo mostram. `noQuadro.previsao` sozinho não basta: o
 * arquivo de previsão também baixa para a ficha do sistema selecionado com a
 * Previsão desligada, e aí nada previsto vai para o mapa inteiro.
 */
export function previsaoDesenhada(
  s: Pick<TrackingState, 'previsao' | 'antecedencia' | 'fluxo' | 'passo' | 'noQuadro' | 'hNoQuadro'> &
    Partial<Pick<TrackingState, 'naObservacao' | 'naOrigem'>> &
    DoQuadro,
  satelite: boolean,
): number {
  if (!s.previsao || s.naOrigem === false || !satelite || s.noQuadro.previsao !== 'ok') return 0;
  const naTela = antecedenciaNaTela(s);
  return naTela > 0 && !!s.hNoQuadro?.includes(naTela) ? naTela : 0;
}

/**
 * Antecedência do contorno previsto SÓ do sistema selecionado (0 = nenhum):
 * Previsão desligada (ou ligada, numa imagem anterior à origem), ficha aberta com uma antecedência escolhida nela, e o
 * arquivo do quadro na tela com essa antecedência. Com a Previsão ligada quem
 * manda é `previsaoDesenhada`, para todos os sistemas.
 */
export function previsaoDoSelecionado(
  s: Pick<TrackingState, 'previsao' | 'selecao' | 'antecedenciaDaSelecao' | 'noQuadro' | 'hNoQuadro'> & Partial<Pick<TrackingState, 'naOrigem'>>,
  satelite: boolean,
): number {
  const h = s.antecedenciaDaSelecao;
  // Com a Previsão ligada, na origem quem manda é a de todos; nas imagens anteriores, só a do clicado.
  if ((s.previsao && s.naOrigem !== false) || !satelite || !s.selecao || h === null || h <= 0 || s.noQuadro.previsao !== 'ok') return 0;
  return s.hNoQuadro?.includes(h) ? h : 0;
}

/** O arquivo de previsão na tela chegou sem nenhum contorno: não há sistema previsto a partir deste horário. */
export function previsaoVazia(s: Pick<TrackingState, 'noQuadro' | 'hNoQuadro'>): boolean {
  return s.noQuadro.previsao === 'ok' && s.hNoQuadro !== null && s.hNoQuadro.length === 0;
}

/** A antecedência como o link a aceita (inteira, de 1 a ANTECEDENCIA_MAX), ou null. Zero é a observação. */
export function antecedenciaParaGuardar(min: number): number | null {
  if (!Number.isFinite(min)) return null;
  const n = Math.round(min);
  if (n <= 0) return 0;
  return n <= ANTECEDENCIA_MAX ? n : null;
}

/** Alguma das duas subcamadas ligada. */
export function emUso(s: Pick<TrackingState, 'rastreio' | 'previsao'> = useTrackingStore.getState()): boolean {
  return s.rastreio || s.previsao;
}

/**
 * Sem parâmetro = o padrão (só o rastreio). `k=0` nada · `k=p` só a previsão ·
 * `k=rp` os dois; `n` = com os núcleos internos (`k=rn`); `p90` =
 * antecedência, só quando difere do padrão.
 */
export function encodeTracking(s: Salvo): string | null {
  if (!s.rastreio && !s.previsao) return '0';
  const nucleos = s.nucleos ?? DEFAULT_NUCLEOS;
  if (s.rastreio === DEFAULT_RASTREIO && s.previsao === DEFAULT_PREVISAO && nucleos === DEFAULT_NUCLEOS) return null;
  // A antecedência 0 (só a observação) é um passo do fluxo, não uma vista para compartilhar.
  // Fora do que o decode aceita (não inteira, acima do teto), fica de fora: o resto do valor não se perde.
  const a = antecedenciaParaGuardar(s.antecedencia);
  const p = s.previsao ? (a !== null && a > 0 && a !== DEFAULT_ANTECEDENCIA ? `p${a}` : 'p') : '';
  return `${s.rastreio ? 'r' : ''}${nucleos ? 'n' : ''}${p}`;
}

/**
 * O que vai para as PREFERÊNCIAS SALVAS (urlSync.ts): o mesmo do link, mais o
 * que o link não leva porque não muda o mapa, mas é escolha do usuário:
 * - a antecedência escolhida com a previsão desligada (`a90`: `k=ra90`);
 * - os núcleos ligados com rastreio e previsão desligados (`k=0n`).
 * Quem religa no dia seguinte encontra tudo onde deixou.
 */
export function persistTracking(s: Salvo): string | null {
  const link = encodeTracking(s);
  const tudoDesligado = !s.rastreio && !s.previsao;
  const base = tudoDesligado && (s.nucleos ?? DEFAULT_NUCLEOS) ? '0n' : link;
  const a = antecedenciaParaGuardar(s.antecedencia);
  if (s.previsao || a === null || a <= 0 || a === DEFAULT_ANTECEDENCIA) return base;
  return `${base ?? 'r'}a${a}`;
}

export function decodeTracking(value: string): Partial<Salvo> | null {
  // Três algarismos bastam: a máquina publica até +180 e o teto é ANTECEDENCIA_MAX.
  const m = /^(0n?|(r?)(n?)(?:(p)(\d{1,3})?)?)(?:a(\d{1,3}))?$/.exec(value);
  if (!m) return null;
  const desligado = m[1].startsWith('0');
  if (!desligado && !m[2] && !m[4]) return null;
  // `k=0` dos links já emitidos não fala dos núcleos, e continua sem falar.
  const out: Partial<Salvo> = desligado
    ? { rastreio: false, previsao: false, ...(m[1] === '0n' ? { nucleos: true } : {}) }
    : { rastreio: m[2] === 'r', previsao: m[4] === 'p', nucleos: m[3] === 'n' };
  const minutos = m[5] ?? (m[4] ? undefined : m[6]);
  if (minutos) {
    const n = Number(minutos);
    if (n >= 1 && n <= ANTECEDENCIA_MAX) out.antecedencia = n;
  }
  return out;
}

registerUrlCodec({
  key: 'k',
  encode: () => encodeTracking(useTrackingStore.getState()),
  persist: () => persistTracking(useTrackingStore.getState()),
  decode: (value) => {
    const s = decodeTracking(value);
    if (s) useTrackingStore.setState(s);
  },
  subscribe: (onChange) =>
    useTrackingStore.subscribe((a, b) => {
      if (a.rastreio !== b.rastreio || a.previsao !== b.previsao || a.antecedencia !== b.antecedencia || a.nucleos !== b.nucleos) onChange();
    }),
});
